import { ApexOptions } from 'apexcharts';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuid } from 'uuid';
import { env } from '../../env';
import {
    AcdEntityListObject,
    AcdEntityNameEnum,
    AcdEntranceVoice,
    AcdGroupNonvoice,
    AcdGroupSummary,
    AcdGroupVoice,
    AcdUser,
    AcdUserSummary,
    CompactType,
    ConfirmDialogModeEnum,
    DataEntityEnum,
    DataTypeSelectItem,
    EditorModeEnum,
    ObmOverview,
    ObmUserRankingData,
    RequestOptions,
    Service,
    SingleValueTextOptions,
    SlaColors,
    Stage,
    ToastTypeEnum,
    WeatherLocationItem,
    Widget,
    WidgetData,
    WidgetEndpoint,
    WidgetType,
    WidgetTypeEnum,
} from '../../models/Index';
import HttpService from '../httpService/HttpService';
import StateService from '../stateService/StateService';

const httpClient = HttpService.getAxiosClient();
const axiosClient = axios.create();

// Basic stuff
const BACKEND_URL = `${env.REACT_APP_BACKEND_URL}/v1`;
const CITY_SEARCH_URL = `${env.REACT_APP_CITY_SEARCH_URL}/v1`;
const DIAPI_ROUTER_URL = `${env.REACT_APP_DIAPI_ROUTER_URL}/v1`;
const PROXY_BASE_URL = `${env.REACT_APP_PROXY_URL}`;
const ENTITY_API_URL = `${env.REACT_APP_ENTITY_API_URL}/v1`;
const CUBEJS_API_URL = `${env.REACT_APP_CUBEJS_API_URL}/v1`;
const RESOURCE_CONFIG_API = `${env.REACT_APP_RESOURCE_CONFIG_API_URL}/v1`;

const getAcdEntityList = (
    entity?: string,
    customerUuid?: string,
    servicetype?: string,
    serviceinstance?: number,
    options?: RequestOptions,
): Promise<AcdEntityListObject[]> => {
    const checkedServiceType = servicetype === 'obm' ? 'dialer' : servicetype;
    const strippedEntity = entity.endsWith('_extra') ? entity.slice(0, -6) : entity;
    /*
        const entityListUrl = `http://feature-data-insights-api-eu1.vier.services/entity/v1/customers/${customerUuid}/servicetype/${checkedServiceType}/serviceinstance/${serviceinstance}/${strippedEntity}`
    */
    const entityListUrl = `${ENTITY_API_URL}/customers/${customerUuid}/servicetype/${checkedServiceType}/serviceinstance/${serviceinstance}/${strippedEntity}`;
    return httpClient
        .get(entityListUrl, { ...options })
        .then((response) => {
            if (response?.data?.length > 0) {
                return response.data;
            }
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_OBM_CAMPAIGN_SUMMARY_USER_RANKING_DATA_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({
                source: 'WIDGET_SERVICE.GET_OBM_CAMPAIGN_SUMMARY_USER_RANKING_DATA',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
            return err.response;
        });
};

function getIframeURL(url: string, options?: RequestOptions): Promise<any> {
    return httpClient
        .get(`${PROXY_BASE_URL}/${url}`, { ...options })
        .then((response) => {
            return response;
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_IFRAME_URL_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'WIDGET_SERVICE.GET_IFRAME_URL', text: msg, type: ToastTypeEnum.ERROR });
            return err;
        });
}

function getRssFeed(url: string, options?: RequestOptions): Promise<any> {
    return axiosClient
        .get(`${PROXY_BASE_URL}/${url}`, { ...options })
        .then((response) => {
            return { data: response.data, status: response.status };
        })
        .catch((err) => {
            // used so that a reload i.e. when the dashboard changes into edit mode won't trigger a 'cancelled' error
            if (axios.isCancel(err)) {
                // Request was cancelled (likely due to page reload)
                return;
            }
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_RSS_FEED_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;

            StateService.toastMessage$.next({
                source: 'WIDGET_SERVICE.GET_RSS_FEED',
                text: msg,
                type: ToastTypeEnum.ERROR,
            });
        });
}

function getWeatherByEndpoint(location: WeatherLocationItem, endpoint: string, options?: RequestOptions): Promise<any> {
    return (
        httpClient
            .get(`${BACKEND_URL}/${endpoint}/lat/${location.lat}/lon/${location.lon}`, { ...options })
            .then((response) => {
                return { data: response.data, status: response.status };
            })
            /* removed until error is understood */
            /*.catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_WEATHER_DATA_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'WIDGET_SERVICE.GET_WEATHER_BY_ENDPOINT', text: msg, type: ToastTypeEnum.ERROR });
        });*/
            .catch((err) => {
                return null;
            })
    );
}

function getWeatherLocationAutoComplete(searchString: string, lat?: number, lon?: number, options?: RequestOptions): Promise<any> {
    /*get besser model instead of any*/
    let URL = `${CITY_SEARCH_URL}/city?name=${searchString}&limit=10`;
    if (lat !== null && lon !== null) {
        URL = `${URL}&lat=${lat}&lon=${lon}`;
    }
    return axiosClient
        .get(URL, { ...options })
        .then((response) => {
            return { data: response.data, status: response.status };
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_WEATHER_LOCATION_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'WIDGET_SERVICE.GET_WEATHER_LOCATION', text: msg, type: ToastTypeEnum.ERROR });
        });
}

function getAcdVoiceGroupData(serviceId: string, options?: RequestOptions): Promise<AcdGroupVoice[]> {
    return httpClient
        .get(`${DIAPI_ROUTER_URL}/macd/service/${serviceId}/voicegroup`, { ...options })
        .then((response) => response.data)
        .catch((err) => {
            return null;
        });
}

function getAcdNonVoiceGroupData(serviceId: string, options?: RequestOptions): Promise<AcdGroupNonvoice[]> {
    return httpClient.get(`${DIAPI_ROUTER_URL}/macd/service/${serviceId}/nonvoicegroup`, { ...options }).then((response) => response.data);
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_NON_VOICE_GROUP_DATA_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({ source: 'WIDGET_SERVICE.GET_NON_VOICE_GROUP_DATA', text: msg, type: ToastTypeEnum.ERROR });
        return err.response;
    });*/
}

function getAcdGroupSummaryData(serviceId: string, options?: RequestOptions): Promise<AcdGroupSummary[]> {
    return httpClient.get(`${DIAPI_ROUTER_URL}/macd/service/${serviceId}/groupsummary`, { ...options }).then((response) => {
        return response.data;
    });
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_GROUP_SUMMARY_DATA_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({ source: 'WIDGET_SERVICE.GET_GROUP_SUMMARY_DATA', text: msg, type: ToastTypeEnum.ERROR });
        return err.response;
    });*/
}

function getAcdUserData(serviceId: string, options?: RequestOptions): Promise<AcdUser[]> {
    return httpClient.get(`${DIAPI_ROUTER_URL}/macd/service/${serviceId}/user`, { ...options }).then((response) => response.data);
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_ACD_USER_DATA_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({ source: 'WIDGET_SERVICE.GET_ACD_USER_DATA', text: msg, type: ToastTypeEnum.ERROR });
        return err.response;
    });*/
}

function getAcdUserSummaryData(serviceId: string, options?: RequestOptions): Promise<AcdUserSummary[]> {
    return httpClient.get(`${DIAPI_ROUTER_URL}/macd/service/${serviceId}/usersummary`, { ...options }).then((response) => {
        return response.data;
    });
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_ACD_USER_SUMMARY_DATA_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({ source: 'WIDGET_SERVICE.GET_ACD_USER_SUMMARY_DATA', text: msg, type: ToastTypeEnum.ERROR });
        return err.response;
    });*/
}

function getAcdVoiceEntranceData(serviceId: string, options?: RequestOptions): Promise<AcdEntranceVoice[]> {
    return httpClient.get(`${DIAPI_ROUTER_URL}/macd/service/${serviceId}/voiceentrance`, { ...options }).then((response) => response.data);
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_ACD_VOICE_ENTRANCE_DATA_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({ source: 'WIDGET_SERVICE.GET_ACD_VOICE_ENTRANCE_DATA', text: msg, type: ToastTypeEnum.ERROR });
        return err.response;
    });*/
}

function getObmCampaignOverviewData(extCustomerId: number, dataEntityId: string, options?: RequestOptions): Promise<ObmOverview> {
    return httpClient
        .get(`${DIAPI_ROUTER_URL}/obm/service/${extCustomerId}/campaign/${dataEntityId}/overview`, { ...options })
        .then((response) => {
            return response.data;
        });
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_OBM_CAMPAIGN_OVERVIEW_DATA_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({
            source: 'WIDGET_SERVICE.GET_OBM_CAMPAIGN_OVERVIEW_DATA',
            text: msg,
            type: ToastTypeEnum.ERROR,
        });
        return err.response;
    });*/
}

function getObmCampaignSummaryOverviewData(extCustomerId: number, dataEntityId: string, options?: RequestOptions): Promise<ObmOverview> {
    return httpClient
        .get(`${DIAPI_ROUTER_URL}/obm/service/${extCustomerId}/campaignsummary/${dataEntityId}/overview`, { ...options })
        .then((response) => response.data);
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_OBM_CAMPAIGN_SUMMARY_OVERVIEW_DATA_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({
            source: 'WIDGET_SERVICE.GET_OBM_CAMPAIGN_SUMMARY_OVERVIEW_DATA',
            text: msg,
            type: ToastTypeEnum.ERROR,
        });
        return err.response;
    });*/
}

function getObmCampaignUserRankingData(
    extCustomerId: string,
    campaignId: string,
    resultCodes: string,
    options?: RequestOptions,
): Promise<ObmUserRankingData> {
    return httpClient
        .get(`${DIAPI_ROUTER_URL}/obm/service/${extCustomerId}/campaign/${campaignId}/userranking/resultcodes/${resultCodes}`, {
            ...options,
        })
        .then((response) => response.data);
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_OBM_CAMPAIGN_USER_RANKING_DATA_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({
            source: 'WIDGET_SERVICE.GET_OBM_CAMPAIGN_USER_RANKING_DATA',
            text: msg,
            type: ToastTypeEnum.ERROR,
        });
        return err.response;
    });*/
}

function getObmCampaignSummaryUserRankingData(
    extCustomerId: string,
    campaignId: string,
    resultCodes: string,
    options?: RequestOptions,
): Promise<ObmUserRankingData> {
    return httpClient
        .get(`${DIAPI_ROUTER_URL}/obm/service/${extCustomerId}/campaignsummary/${campaignId}/userranking/resultcodes/${resultCodes}`, {
            ...options,
        })
        .then((response) => response.data);
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_OBM_CAMPAIGN_SUMMARY_USER_RANKING_DATA_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({
            source: 'WIDGET_SERVICE.GET_OBM_CAMPAIGN_SUMMARY_USER_RANKING_DATA',
            text: msg,
            type: ToastTypeEnum.ERROR,
        });
        return err.response;
    });*/
}

function getServices(customerUuid: string, options?: RequestOptions): Promise<Service[]> {
    return httpClient.get(`${BACKEND_URL}/services`, { ...options, headers: { customerUuid } }).then((response) => {
        if (response?.data?.length > 0) {
            return response.data;
        }
    });
    /*.catch((err) => {
        const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_SERVICES_TEXT';
        const msg = err.response?.data ? err.response.data : defaultText;
        StateService.toastMessage$.next({ source: 'WIDGET_SERVICE.GET_SERVICES', text: msg, type: ToastTypeEnum.ERROR });
        return err.response;
    });*/
}

function getInitialWidgetData(): WidgetData {
    const singleValueInitialTextValues = {
        color: '',
        bgColor: '',
        bold: false,
        italic: false,
        underlined: false,
    } as SingleValueTextOptions;

    return {
        backgroundColor: '#ffffff',
        borderColor: '#bec9d7',
        bottomThreshold: singleValueInitialTextValues,
        chartData: {},
        columnChartResourceId: '',
        doughnutChartResourceId: '',
        lineChartResourceId: '',
        radarChartResourceId: '',
        data: [{}],
        displayColorPicker: '',
        fontColor: '#070007',
        interval: 20000,
        isBorderColor: true,
        isUsingDefault: true,
        key: '1',
        normalThreshold: singleValueInitialTextValues,
        presentationStyle: 'number',
        title: '',
        titleFontSize: '1.5rem',
        upThreshold: singleValueInitialTextValues,
        type: WidgetTypeEnum.NOT_SELECTED,
    } as WidgetData;
}

function getDataTypesForWidgetEditorV2(type, dataEntity): DataTypeSelectItem[] {
    const obmOverviewTextFields = [
        { text: 'ACTIVE_CALLS', value: 'activeCalls', isShown: true },
        { text: 'AVERAGE_CALL_TIME', value: 'averageCallTime', isShown: true },
        { text: 'AVERAGE_WAITING_TIME', value: 'averageWaitingTime', isShown: true },
        { text: 'AVERAGE_WRAP_UP_TIME', value: 'averageWrapUpTime', isShown: true },
        { text: 'OFFERED', value: 'offered', isShown: true },
        { text: 'TO_GO_60', value: 'toGo60', isShown: true },
        { text: 'TO_GO_ALL', value: 'toGoAll', isShown: true },
        { text: 'TO_GO_FINISHED', value: 'toGoFinished', isShown: true },
        { text: 'USERS_FREE', value: 'usersFree', isShown: true },
        { text: 'USERS_IN_WRAP_UP', value: 'usersInWrapUp', isShown: true },
        { text: 'USERS_LOGGED_IN', value: 'usersLoggedIn', isShown: true },
        { text: 'USERS_PAUSE', value: 'usersPause', isShown: true },
    ];

    const obmUserRankingTextFields = [
        { text: 'CONTACTS_FINISHED', value: 'contactsFinished', isShown: true },
        { text: 'TRANSACTION_SUCCESSFUL', value: 'transactionSuccessful', isShown: true },
        { text: 'TRANSACTION_REJECTED', value: 'transactionRejected', isShown: true },
        { text: 'SUCCESS_SCORE', value: 'successScore', isShown: true },
    ];

    return {
        acd: {
            group: [
                { text: 'ACCEPTED_CALLS', value: 'acceptedCalls', isShown: true },
                { text: 'AGENTS_BREAK', value: 'agentsBreak', isShown: true },
                { text: 'AGENTS_BUSY', value: 'agentsBusy', isShown: true },
                { text: 'AGENTS_FREE', value: 'agentsFree', isShown: true },
                { text: 'AGENTS_LOGGED_IN', value: 'agentsLoggedIn', isShown: true },
                { text: 'AGENTS_PRODUCTIVE_BREAK', value: 'agentsProductiveBreak', isShown: true },
                { text: 'AGENTS_REFINISH', value: 'agentsRefinish', isShown: true },
                { text: 'AGENTS_RESERVED', value: 'agentsReserved', isShown: true },
                { text: 'AVAILABILITY', value: 'availability', isShown: true },
                { text: 'AVAILABILITY_SINCE_MIDNIGHT', value: 'availability_00', isShown: true },
                { text: 'AVAILABLE_TIME_SINCE_MIDNIGHT', value: 'availableTimeSinceMidnight', isShown: true },
                { text: 'AVERAGE_CALL_TIME', value: 'averageCallTime', isShown: true },
                { text: 'AVERAGE_CALL_TIME_00', value: 'averageCallTime_00', isShown: true },
                { text: 'AVERAGE_HANDLING_TIME_IN_SEC', value: 'averageHandlingTimeInSec', isShown: true },
                { text: 'AVERAGE_HANDLING_TIME_SINCE_MIDNIGHT', value: 'averageHandlingTimeSinceMidnight', isShown: true },
                { text: 'AVERAGE_WRAP_UP_TIME', value: 'averageWrapUpTime', isShown: true },
                { text: 'AVERAGE_WRAP_UP_TIME_00', value: 'averageWrapUpTime_00', isShown: true },
                {
                    text: 'CALLS_ABORTED_BY_CALLER_INBOUND_SINCE_MIDNIGHT',
                    value: 'callsAbortedByCallerInboundSinceMidnight',
                    isShown: true,
                },
                { text: 'CALLS_IN_PROGRESS', value: 'callsInProgress', isShown: true },
                { text: 'CALLS_LESS_THAN_X_SEC_INBOUND_SINCE_MIDNIGHT', value: 'callsLessThanXSecInboundSinceMidnight', isShown: true },
                { text: 'CALLS_SINCE_MIDNIGHT', value: 'callsSinceMidnight', isShown: true },
                { text: 'CURRENT_CALLS', value: 'callsTotal', isShown: true },
                { text: 'CALLS_WAITING', value: 'callsWaiting', isShown: true },
                { text: 'HANDLED_CALLS_SINCE_MIDNIGHT', value: 'handledCallsSinceMidnight', isShown: true },
                { text: 'ITEMS_MAX_WAIT', value: 'itemsMaxWait', isShown: true },
                { text: 'OPEN_RESUBMISSIONS', value: 'openResubmissions', isShown: true },
                { text: 'SERVICE_LEVEL', value: 'serviceLevel', isShown: true },
                { text: 'SERVICE_LEVEL_SINCE_MIDNIGHT', value: 'serviceLevel_00', isShown: true },
                {
                    text: 'TIME_UNTIL_CALLS_ABORTED_BY_CALLER_INBOUND_SINCE_MIDNIGHT',
                    value: 'timeUntilCallsAbortedByCallerInboundSinceMidnight',
                    isShown: true,
                },
                {
                    text: 'TIME_UNTIL_CALLS_HANDLED_INBOUND_SINCE_MIDNIGHT',
                    value: 'timeUntilCallsHandledInboundSinceMidnight',
                    isShown: true,
                },
                { text: 'UMS_MESSAGES_SINCE_MIDNIGHT', value: 'umsMessagesSinceMidnight', isShown: true },
                { text: 'W_TIME_IN_REAL_QUEUE_SINCE_MIDNIGHT', value: 'wTimeInRealQueueSinceMidnight', isShown: true },
            ],
            group_extra: [
                { text: 'ACCEPTED_CALLS', value: 'acceptedCalls', isShown: true },
                { text: 'AGENTS_BREAK', value: 'agentsBreak', isShown: true },
                { text: 'AGENTS_BUSY', value: 'agentsBusy', isShown: true },
                { text: 'AGENTS_FREE', value: 'agentsFree', isShown: true },
                { text: 'AGENTS_LOGGED_IN', value: 'agentsLoggedIn', isShown: true },
                { text: 'AGENTS_PRODUCTIVE_BREAK', value: 'agentsProductiveBreak', isShown: true },
                { text: 'AGENTS_REFINISH', value: 'agentsRefinish', isShown: true },
                { text: 'AGENTS_RESERVED', value: 'agentsReserved', isShown: true },
                { text: 'AVAILABILITY', value: 'availability', isShown: true },
                { text: 'AVAILABILITY_SINCE_MIDNIGHT', value: 'availability_00', isShown: true },
                { text: 'AVAILABLE_TIME_SINCE_MIDNIGHT', value: 'availableTimeSinceMidnight', isShown: true },
                { text: 'AVERAGE_CALL_TIME', value: 'averageCallTime', isShown: true },
                { text: 'AVERAGE_CALL_TIME_00', value: 'averageCallTime_00', isShown: true },
                { text: 'AVERAGE_HANDLING_TIME_IN_SEC', value: 'averageHandlingTimeInSec', isShown: true },
                { text: 'AVERAGE_HANDLING_TIME_SINCE_MIDNIGHT', value: 'averageHandlingTimeSinceMidnight', isShown: true },
                { text: 'AVERAGE_WAITING_TIME_IN_REAL_QUEUE', value: 'averageWaitingTimeInRealQueue', isShown: true },
                { text: 'AVERAGE_WRAP_UP_TIME', value: 'averageWrapUpTime', isShown: true },
                { text: 'AVERAGE_WRAP_UP_TIME_00', value: 'averageWrapUpTime_00', isShown: true },
                {
                    text: 'CALLS_ABORTED_BY_CALLER_INBOUND_SINCE_MIDNIGHT',
                    value: 'callsAbortedByCallerInboundSinceMidnight',
                    isShown: true,
                },
                { text: 'CALLS_IN_PROGRESS', value: 'callsInProgress', isShown: true },
                { text: 'CALLS_LESS_THAN_X_SEC_INBOUND_SINCE_MIDNIGHT', value: 'callsLessThanXSecInboundSinceMidnight', isShown: true },
                { text: 'CALLS_MAX_WAIT', value: 'callsMaxWait', isShown: true },
                { text: 'CALLS_SINCE_MIDNIGHT', value: 'callsSinceMidnight', isShown: true },
                { text: 'CALLS_TOTAL', value: 'callsTotal', isShown: true },
                { text: 'CALLS_WAITING', value: 'callsWaiting', isShown: true },
                { text: 'HANDLED_CALLS_SINCE_MIDNIGHT', value: 'handledCallsSinceMidnight', isShown: true },
                { text: 'SERVICE_LEVEL', value: 'serviceLevel', isShown: true },
                { text: 'SERVICE_LEVEL_SINCE_MIDNIGHT', value: 'serviceLevel_00', isShown: true },
                {
                    text: 'TIME_UNTIL_CALLS_ABORTED_BY_CALLER_INBOUND_SINCE_MIDNIGHT',
                    value: 'timeUntilCallsAbortedByCallerInboundSinceMidnight',
                    isShown: true,
                },
                {
                    text: 'TIME_UNTIL_CALLS_HANDLED_INBOUND_SINCE_MIDNIGHT',
                    value: 'timeUntilCallsHandledInboundSinceMidnight',
                    isShown: true,
                },
                { text: 'UMS_MESSAGES_SINCE_MIDNIGHT', value: 'umsMessagesSinceMidnight', isShown: false },
                { text: 'W_TIME_IN_REAL_QUEUE_SINCE_MIDNIGHT', value: 'wTimeInRealQueueSinceMidnight', isShown: true },
            ],
            group_summary: [
                { text: 'AGENT_FREI_ORANGE', value: 'agentFreiOrange', isShown: false },
                { text: 'AGENT_FREI_RED', value: 'agentFreiRot', isShown: false },
                { text: 'AGENTS_FREE', value: 'agentsFree', isShown: true },
                { text: 'AVAILABILITY', value: 'availability', isShown: true },
                { text: 'AVAILABILITY_SINCE_MIDNIGHT', value: 'availability_00', isShown: true },
                { text: 'AVERAGE_CALL_TIME', value: 'averageCallTime', isShown: true },
                { text: 'AVERAGE_CALL_TIME_00', value: 'averageCallTime_00', isShown: true },
                { text: 'CALLS_CONNECTING', value: 'callsConnecting', isShown: true },
                { text: 'CALLS_IN_PROGRESS', value: 'callsInProgress', isShown: true },
                { text: 'CALLS_MAX_WAIT', value: 'callsMaxWait', isShown: true },
                { text: 'CALLS_SINCE_MIDNIGHT', value: 'callsSinceMidnight', isShown: true },
                { text: 'CALLS_TOTAL', value: 'callsTotal', isShown: true },
                { text: 'CALLS_WAITING', value: 'callsWaiting', isShown: true },
                { text: 'ERREICHB_ORANGE', value: 'erreichbOrange', isShown: false },
                { text: 'ERRICHB_RED', value: 'erreichbRot', isShown: false },
                { text: 'HANDLED_CALLS_SINCE_MIDNIGHT', value: 'handledCallsSinceMidnight', isShown: true },
                { text: 'PP_ID', value: 'ppId', isShown: false },
                { text: 'QUEUE_ORANGE', value: 'queueOrange', isShown: false },
                { text: 'QUEUE_ROT', value: 'queueRot', isShown: false },
                { text: 'SERVICE_LEVEL', value: 'serviceLevel', isShown: true },
                { text: 'SERVICE_LEVEL_SINCE_MIDNIGHT', value: 'serviceLevel_00', isShown: true },
                { text: 'SERVICE_LEVEL_ORANGE', value: 'servLevelOrange', isShown: false },
                { text: 'SERVICE_LEVEL_ROT', value: 'servLevelRot', isShown: false },
            ],
            user: [
                { text: 'NAME', value: 'name', isShown: true },
                { text: 'HANDLED_CALLS_SINCE_MIDNIGHT', value: 'handledCallsSinceMidnight', isShown: true },
                { text: 'PHONE', value: 'phone', isShown: true },
                { text: 'STATUS_TIME_UTC', value: 'statusTimeUTC', isShown: true },
                { text: 'STATUS_TIME', value: 'statusTime', isShown: true },
                { text: 'LAST_LOGIN_PERIOD', value: 'lastLoginPeriod', isShown: true },
                { text: 'LAST_LOGIN_UTC', value: 'lastLoginUTC', isShown: true },
                { text: 'AGENT_NO_CONNECT_LAST_DISCONNENT_CAUSE', value: 'agentNoConnectLastDisconnectCause', isShown: true },
                { text: 'LAST_LOGIN', value: 'lastLogin', isShown: true },
                { text: 'AVERAGE_CALL_TIME_DIVIDEND', value: 'averageCallTime_Dividend', isShown: true },
                { text: 'AVERAGE_CALL_TIME_DIVISOR', value: 'averageCallTime_Divisor', isShown: true },
                { text: 'AVERAGE_CALL_TIME_00_DIVISOR', value: 'averageCallTime_00_Divisor', isShown: true },
                { text: 'AVERAGE_CALL_TIME_00_DIVIDEND', value: 'averageCallTime_00_Dividend', isShown: true },
                { text: 'EMS_CURRENT_CHANNEL', value: 'ems_Current_Channel', isShown: true },
            ],
            user_summary: [
                { text: 'NAME', value: 'name', isShown: false },
                { text: 'PP_ID', value: 'ppId', isShown: false },
                { text: 'AGENTS_BREAK', value: 'agentsBreak', isShown: true },
                { text: 'AGENTS_BUSY', value: 'agentsBusy', isShown: true },
                { text: 'AGENTS_DIALER', value: 'agentsDialer', isShown: true },
                { text: 'AGENTS_FREE', value: 'agentsFree', isShown: true },
                { text: 'AGENTS_LOGGED_IN', value: 'agentsLoggedIn', isShown: true },
                { text: 'AGENTS_PRODUCTIVE_BREAK', value: 'agentsProductiveBreak', isShown: true },
                { text: 'AGENTS_REFINISH', value: 'agentsRefinish', isShown: true },
                { text: 'AGENTS_RESERVED', value: 'agentsReserved', isShown: true },
                { text: 'AVERAGE_CALL_TIME', value: 'averageCallTime', isShown: true },
                { text: 'AVERAGE_CALL_TIME_00', value: 'averageCallTime_00', isShown: true },
                { text: 'HANDLED_CALLS_SINCE_MIDNIGHT', value: 'handledCallsSinceMidnight', isShown: true },
            ],
            entrance: [
                { text: 'NAME', value: 'name', isShown: false },
                { text: 'TYPE', value: 'type', isShown: false },
                { text: 'SERVICE_NR', value: 'serviceNr', isShown: false },
                { text: 'MAX_WAITING', value: 'maxWaiting', isShown: true },
                {
                    text: 'TIME_UNTIL_CALLS_ABORTED_BY_CALLER_INBOUND_SINCE_MIDNIGHT',
                    value: 'timeUntilCallsAbortedByCallerSinceMidnight',
                    isShown: true,
                },
                { text: 'AVAILABILITY', value: 'erreichbarkeit', isShown: true },
                { text: 'SERVICE_LEVEL_00', value: 'servicelevel_00', isShown: true },
                { text: 'CALLS_IN_PROGRESS', value: 'callsInProgress', isShown: true },
                { text: 'CALLS_OTHER_STATE', value: 'callsOtherState', isShown: true },
                { text: 'AVERAGE_HANDLING_TIME_SINCE_MIDNIGHT', value: 'averageHandlingTimeSinceMidnight', isShown: true },
                { text: 'CALLS_ABORTED_BY_CALLER_INBOUND_SINCE_MIDNIGHT', value: 'callsAbortedByCallerSinceMidnight', isShown: true },
                { text: 'SERVICE_LEVEL', value: 'servicelevel', isShown: true },
                { text: 'TAKEOVERS_SINCE_MIDNIGHT', value: 'takeOversSinceMidnight', isShown: true },
                { text: 'CALLS_REFERRED_TO_BUSY_SINCE_MIDNIGHT', value: 'callsReferredToBusySinceMidnight', isShown: true },
                { text: 'TIME_UNTIL_CALLS_HANDLED_INBOUND_SINCE_MIDNIGHT', value: 'timeUntilCallsHandledSinceMidnight', isShown: true },
                { text: 'CALLS_RINGING', value: 'callsRinging', isShown: true },
                { text: 'CALLS_WAITING', value: 'callsWaiting', isShown: true },
                { text: 'AVERAGE_ABORTED_BY_CALLER_TIME_SINCE_MIDNIGHT', value: 'averageAbortedByCallerTimeSinceMidnight', isShown: true },
                { text: 'CALLS_LESS_THAN_X_SEC_INBOUND_SINCE_MIDNIGHT', value: 'callsLessThanXSecSinceMidnight', isShown: true },
                { text: 'HANDLED_CALLS_SINCE_MIDNIGHT', value: 'handledCallsSinceMidnight', isShown: true },
                { text: 'AVAILABILITY_00', value: 'erreichbarkeit_00', isShown: true },
                { text: 'CALLS_TOTAL', value: 'callsTotal', isShown: true },
            ],
        },
        obm: {
            campaign: obmOverviewTextFields,
            campaign_summary: obmOverviewTextFields,
            campaign_extra: obmUserRankingTextFields,
            campaign_summary_extra: obmUserRankingTextFields,
        },
    }[type][dataEntity];
}

// resource config

// const getAllResources = (customerUuid: any, options: any): Promise<any> => {
//     try {
//         const response = httpClient.get(`${CUBEJS_API_URL}/customers/${customerUuid}/resources`, { ...options });
//         return response;
//     } catch (err) {
//         console.error('err', err);
//     }
// };

const getResourceConfigResources = (options: any): Promise<any> => {
    try {
        const response = httpClient.get(`${RESOURCE_CONFIG_API}/resources`, { ...options });
        return response;
    } catch (err) {
        console.error('err', err);
    }
};

const getResourceConfigResourceById = (resourceId: string, options: any): Promise<any> => {
    try {
        const response = httpClient.get(`${RESOURCE_CONFIG_API}/resources/${resourceId}`, { ...options });
        return response;
    } catch (err) {
        console.error('err', err);
    }
};

const getSelectedResourceData = (customerUuid: string, resourceId: string, options: {}) => {
    try {
        const response = httpClient.get(`${CUBEJS_API_URL}/customers/${customerUuid}/resources/${resourceId}`, { ...options });
        return response;
    } catch (err) {
        console.error('err', err);
    }
};

const getQueryPeriodsById = (resourceId: string, options: any): Promise<any> => {
    try {
        const response = httpClient.get(`${RESOURCE_CONFIG_API}/resources/${resourceId}/queryperiods`, { ...options });
        return response;
    } catch (err) {
        console.error('err', err);
    }
};

const getResourceData = (
    customerUuid: string,
    resourceId: string,
    queryPeriodId: string,
    dimensionId: string,
    options: any,
): Promise<any> => {
    if (dimensionId !== undefined && queryPeriodId !== undefined && resourceId !== undefined && customerUuid) {
        try {
            const response = httpClient.get(
                `${RESOURCE_CONFIG_API}/tenants/${customerUuid}/cubejs/resources/${resourceId}/queryPeriod/${queryPeriodId}/dimension/${dimensionId}`,
                { ...options },
            );
            return response;
        } catch (err) {
            console.error('err', err);
        }
    }
};

function getWidgetTypes(): WidgetType[] {
    return [
        { type: WidgetTypeEnum.ACD_WALLBOARD_BOTTOM, amount: 6 },
        { type: WidgetTypeEnum.ACD_WALLBOARD_RIGHT, amount: 6 },
        { type: WidgetTypeEnum.BAR_CHART, amount: 0 },
        { type: WidgetTypeEnum.COLUMN_CHART, amount: 0 },
        { type: WidgetTypeEnum.DOUGHNUT_CHART, amount: 0 },
        { type: WidgetTypeEnum.CLOCK, amount: 0 },
        { type: WidgetTypeEnum.FREE_TEXT, amount: 0 },
        { type: WidgetTypeEnum.IFRAME, amount: 0 },
        { type: WidgetTypeEnum.LINE_CHART, amount: 0 },
        { type: WidgetTypeEnum.NOT_SELECTED, amount: 0 },
        { type: WidgetTypeEnum.OBM_CAMPAIGN_OVERVIEW, amount: 0 },
        { type: WidgetTypeEnum.OBM_USER_RANKING, amount: 0 },
        { type: WidgetTypeEnum.PICTURE, amount: 0 },
        { type: WidgetTypeEnum.RADAR_CHART, amount: 0 },
        { type: WidgetTypeEnum.RSS_FEED, amount: 0 },
        { type: WidgetTypeEnum.SINGLE_VALUE, amount: 1 },
        { type: WidgetTypeEnum.VIDEO, amount: 0 },
        { type: WidgetTypeEnum.WEATHER, amount: 0 },
    ];
}

function getDefaultValues(type: string): WidgetEndpoint[] {
    return {
        ACD_WALLBOARD_BOTTOM: [{ value: '' }, { value: '' }, { value: '' }, { value: '' }, { value: '' }, { value: '' }],
        ACD_WALLBOARD_RIGHT: [{ value: '' }, { value: '' }, { value: '' }, { value: '' }, { value: '' }, { value: '' }],
        CLOCK: [{ timezone: 'Europe/Berlin', visualization: 'digital' }] as WidgetEndpoint[],
        FREE_TEXT: [
            {
                applyInput: true,
                freeText: '',
                freeTextIsAlignCenter: false,
                freeTextIsAlignLeft: true,
                freeTextIsAlignRight: false,
                freeTextIsBold: false,
                freeTextIsItalic: false,
                freeTextIsUnderlined: false,
            },
        ] as WidgetEndpoint[],
        IFRAME: [{ iframeLink: '', iframeBorder: false }] as WidgetEndpoint[],
        NOT_SELECTED: [],
        OBM_CAMPAIGN_OVERVIEW: [{}] as WidgetEndpoint[],
        OBM_USER_RANKING: [{}] as WidgetEndpoint[],
        PICTURE: [{ pictureLink: '', picturePosition: 'height' }] as WidgetEndpoint[],
        RSS_FEED: [
            {
                applyInput: false,
                divider: '+++',
                limitFeeds: false,
                limitFeedsAmount: 1,
                link: '',
                provideLink: false,
                speed: 1,
                teaserText: false,
            },
        ] as WidgetEndpoint[],
        SINGLE_VALUE: [{ value: '' }],
        VIDEO: [{ applyInput: false, link: '', videoAutoplay: false }] as WidgetEndpoint[],
        WEATHER: [{ location: {}, setLocationAsTitle: false }] as WidgetEndpoint[],
    }[type];
}

function getGroupTypes(serviceType: string, widgetType: string): any[] {
    if (widgetType === WidgetTypeEnum.SINGLE_VALUE) {
        return {
            acd: [
                { text: 'ACD_VOICE_ENTRANCE', dataEntity: AcdEntityNameEnum.ACD_ENTRANCE_VOICE },
                { text: 'ACD_NON_VOICE', dataEntity: AcdEntityNameEnum.ACD_GROUP_NONVOICE },
                { text: 'ACD_Z_GROUPS', dataEntity: AcdEntityNameEnum.ACD_GROUP_SUMMARY },
                { text: 'ACD_VOICE', dataEntity: AcdEntityNameEnum.ACD_GROUP_VOICE },
                { text: 'ACD_USER', dataEntity: AcdEntityNameEnum.ACD_USER },
                { text: 'ACD_USER_SUMMARY', dataEntity: AcdEntityNameEnum.ACD_USER_SUMMARY },
            ],
            obm: [
                { text: 'OBM_CAMPAIGN_OVERVIEW', dataEntity: AcdEntityNameEnum.OBM_OVERVIEW },
                { text: 'OBM_CAMPAIGN_SUMMARY_OVERVIEW', dataEntity: AcdEntityNameEnum.OBM_SUMMARY_OVERVIEW },
                { text: 'OBM_CAMPAIGN_SUMMARY_USER_RANKING', dataEntity: AcdEntityNameEnum.OBM_SUMMARY_USER_RANKING },
                { text: 'OBM_CAMPAIGN_USER_RANKING', dataEntity: AcdEntityNameEnum.OBM_USER_RANKING },
            ],
        }[serviceType];
    } else if (widgetType === WidgetTypeEnum.ACD_WALLBOARD_BOTTOM || widgetType === WidgetTypeEnum.ACD_WALLBOARD_RIGHT) {
        return {
            acd: [
                { text: 'ACD_VOICE_ENTRANCE', dataEntity: AcdEntityNameEnum.ACD_ENTRANCE_VOICE },
                { text: 'ACD_NON_VOICE', dataEntity: AcdEntityNameEnum.ACD_GROUP_NONVOICE },
                { text: 'ACD_Z_GROUPS', dataEntity: AcdEntityNameEnum.ACD_GROUP_SUMMARY },
                { text: 'ACD_VOICE', dataEntity: AcdEntityNameEnum.ACD_GROUP_VOICE },
            ],
        }[serviceType];
    } else if (widgetType === WidgetTypeEnum.OBM_CAMPAIGN_OVERVIEW) {
        return {
            obm: [
                { text: 'OBM_CAMPAIGN_OVERVIEW', dataEntity: AcdEntityNameEnum.OBM_OVERVIEW },
                { text: 'OBM_CAMPAIGN_SUMMARY_OVERVIEW', dataEntity: AcdEntityNameEnum.OBM_SUMMARY_OVERVIEW },
            ],
        }[serviceType];
    } else if (widgetType === WidgetTypeEnum.OBM_USER_RANKING) {
        return {
            obm: [
                { text: 'OBM_CAMPAIGN_SUMMARY_USER_RANKING', dataEntity: AcdEntityNameEnum.OBM_SUMMARY_USER_RANKING },
                { text: 'OBM_CAMPAIGN_USER_RANKING', dataEntity: AcdEntityNameEnum.OBM_USER_RANKING },
            ],
        }[serviceType];
    }
}

function getSlaColors(): SlaColors {
    return { green: '#49BA4E', yellow: '#F5BC00', red: '#BC0000' };
}

function getWallboardGaugeOptions(colors: string[], text: string): ApexOptions {
    return {
        chart: {
            height: 250,
            toolbar: {
                show: false,
            },
        },
        colors: colors,
        plotOptions: {
            radialBar: {
                startAngle: -135,
                endAngle: 135,
                hollow: {
                    margin: 1,
                    size: '70%',
                    background: '#fff',
                    image: undefined,
                    imageOffsetX: 0,
                    imageOffsetY: 0,
                    position: 'front',
                    dropShadow: {
                        enabled: true,
                        top: 2,
                        left: 0,
                        blur: 4,
                        opacity: 0.15,
                    },
                },
                track: {
                    background: '#fff', // '#e4e8ee'
                    strokeWidth: '50%',
                    margin: 0, // margin is in pixels
                    dropShadow: {
                        enabled: true,
                        top: 2,
                        left: 0,
                        blur: 4,
                        opacity: 0.15,
                    },
                },
                dataLabels: {
                    show: true,
                    name: {
                        offsetY: -6,
                        show: true,
                        color: '#888', // #212121
                        fontSize: '9px',
                    },
                    value: {
                        offsetY: 8,
                        // color: '#212121',
                        fontSize: '22px',
                        show: true,
                    },
                },
            },
        },
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'dark',
                type: 'horizontal',
                shadeIntensity: 0.5,
                gradientToColors: colors,
                inverseColors: true,
                opacityFrom: 1,
                opacityTo: 1,
                stops: [0, 100],
            },
        },
        stroke: {
            lineCap: 'butt',
        },
        labels: [text],
    } as ApexOptions;
}

function getNewWidget(
    compactType: CompactType,
    currentMaxIndex: number,
    length: number,
    granularityLevel: number,
    propChartType?: string | null,
): Widget {
    // Default values for the grid (adjustable)
    const GRID_COLS = 12; // Maximum number of columns in the grid

    // Default size
    let w = 2 * granularityLevel;
    let h = 2;

    // Set maximum size for SUPERSET_DASHBOARD
    if (propChartType === 'SUPERSET_DASHBOARD') {
        w = GRID_COLS; // Maximum width (all columns)
        // Height unlimited or set to a larger default value (e.g., 10, without pixel limitation)
        h = 10.5;
    }

    return {
        i: uuid().toString(),
        h: h,
        static: false,
        w: w,
        x: (length * 2) % GRID_COLS, // Adjust positioning
        y: compactType === null ? 0 : Infinity,
    } as Widget;
}

// function getNewWidget(
//     compactType: CompactType,
//     currentMaxIndex: number,
//     length: number,
//     granularityLevel: number,
//     propChartType?: string | null,
// ): Widget {
//     return {
//         i: uuid().toString(),
//         h: 2,
//         static: false,
//         w: 2 * granularityLevel,
//         x: (length * 2) % 12,
//         y: compactType === null ? 0 : Infinity,
//     } as Widget;
// }

function removeWidget(selectedStage: Stage, selectedWidgetData: WidgetData) {
    // Collect necessary data
    const tempWidgetData = cloneDeep(selectedStage.workInProgressWidgetData) as WidgetData[];
    const tempSelectedStage = cloneDeep(selectedStage) as Stage;
    const tempWidgets = cloneDeep(selectedStage.workInProgressGridConfig?.layouts?.lg) as Widget[];

    // Compute indices
    const tempWidgetItem = selectedStage.workInProgressGridConfig?.layouts?.lg.filter((item) => item.i === selectedWidgetData.key)[0];
    const tempWidgetDataItem = selectedStage.workInProgressWidgetData?.filter((item) => item.key === selectedWidgetData.key)[0];
    const widgetIndex = selectedStage.workInProgressGridConfig?.layouts?.lg.indexOf(tempWidgetItem);
    const widgetDataIndex = selectedStage.workInProgressWidgetData?.indexOf(tempWidgetDataItem);

    // Remove data items from widgetData-array and widget-array (=grid-info)
    tempWidgets.splice(widgetIndex, 1);
    tempWidgetData.splice(widgetDataIndex, 1);

    // Update selected stage
    tempSelectedStage.workInProgressWidgetData = tempWidgetData;
    tempSelectedStage.workInProgressGridConfig.layouts.lg = tempWidgets;
    StateService.selectedStage$.next(cloneDeep(tempSelectedStage));

    // Set app-state
    StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.CLOSED);
    StateService.editorMode$.next(EditorModeEnum.CLOSED);
}

const WidgetService = {
    axiosClient,
    getAcdEntityList,
    getAcdGroupSummaryData,
    getAcdNonVoiceGroupData,
    getAcdUserData,
    getAcdUserSummaryData,
    getAcdVoiceEntranceData,
    getAcdVoiceGroupData,
    getResourceConfigResources,
    getResourceConfigResourceById,
    getResourceData,
    getDataTypesForWidgetEditorV2,
    getDefaultValues,
    getGroupTypes,
    getIframeURL,
    getInitialWidgetData,
    getNewWidget,
    getObmCampaignOverviewData,
    getObmCampaignSummaryOverviewData,
    getObmCampaignSummaryUserRankingData,
    getObmCampaignUserRankingData,
    getQueryPeriodsById,
    getRssFeed,
    getSelectedResourceData,
    getServices,
    getSlaColors,
    getWallboardGaugeOptions,
    getWeatherByEndpoint,
    getWeatherLocationAutoComplete,
    getWidgetTypes,
    httpClient,
    removeWidget,
};

export default WidgetService;
